import ReactSelect, {
  ControlProps,
  CSSObjectWithLabel,
  GroupBase,
  MultiValueProps,
  OptionProps,
  Props as ReactSelectProps,
  SingleValueProps,
  StylesConfig,
} from "react-select";
import AsyncSelect, { AsyncProps } from "react-select/async";
import useI18n from "../../../hooks/useTranslations";
import { isHiddenVisibilityOption } from "../VisibilityScope";

interface CustomSelectProps<T, U extends boolean = false> extends Omit<
  ReactSelectProps<T, U>,
  "styles" | "menuPosition" | "getOptionLabel"
> {
  isInvalid?: boolean;
  getOptionLabel: (option: T) => string;
}

export function CustomSelect<T, U extends boolean = false>({
  isInvalid = false,
  value,
  getOptionLabel,
  placeholder,
  ...restProps
}: CustomSelectProps<T, U>) {
  const i18n = useI18n();

  const isHidden = isHiddenVisibilityOption(value);

  const getLabel = (option: T): string => {
    const label = getOptionLabel(option);
    if (!isHiddenVisibilityOption(option)) {
      return label;
    }

    return `${label} - ⚠`;
  };

  return (
    <ReactSelect
      {...restProps}
      value={value}
      getOptionLabel={getLabel}
      placeholder={placeholder ?? `${i18n.translation.common.select}...`}
      styles={reactSelectStyles<T, U>({
        isInvalid,
        isWarning: isHidden,
      })}
      menuPosition="fixed"
      menuPortalTarget={document.body}
    />
  );
}
interface CustomAsyncSelectProps<T, U extends boolean = false, G extends GroupBase<T> = GroupBase<T>> extends Omit<
  AsyncProps<T, U, G>,
  "styles" | "menuPosition" | "getOptionLabel"
> {
  isInvalid?: boolean;
  getOptionLabel: (option: T) => string;
}

export function CustomAsyncSelect<T, U extends boolean = false, G extends GroupBase<T> = GroupBase<T>>({
  isInvalid = false,
  value,
  getOptionLabel,
  placeholder,
  ...restProps
}: CustomAsyncSelectProps<T, U, G>) {
  const i18n = useI18n();

  const isHidden = isHiddenVisibilityOption(value);

  const getLabel = (option: T): string => {
    const label = getOptionLabel(option);
    if (!isHiddenVisibilityOption(option)) {
      return label;
    }

    return `${label} - ⚠`;
  };

  return (
    <AsyncSelect<T, U, G>
      {...restProps}
      value={value}
      getOptionLabel={getLabel}
      placeholder={placeholder ?? `${i18n.translation.common.select}...`}
      styles={reactSelectStyles<T, U, G>({
        isInvalid,
        isWarning: isHidden,
      })}
      menuPosition="fixed"
      menuPortalTarget={document.body}
    />
  );
}

type ReactSelectStyleProps = {
  isInvalid?: boolean;
  isWarning?: boolean;
};

const reactSelectStyles = <T, U extends boolean = false, G extends GroupBase<T> = GroupBase<T>>(
  styleProps?: ReactSelectStyleProps,
): StylesConfig<T, U, G> => ({
  container: (base: CSSObjectWithLabel) => ({
    ...base,
    flex: 1,
  }),
  control: (base: CSSObjectWithLabel, props: ControlProps<T, U, G>) => ({
    ...base,
    cursor: props.isDisabled ? "not-allowed" : "pointer",
    backgroundColor: props.isDisabled ? "var(--grey-10)" : styleProps?.isWarning ? "var(--warning)" : undefined,
    borderRadius: 0,
    border: styleProps?.isInvalid ? "1px solid var(--red)" : "1px solid var(--grey-15)",
    fontWeight: 300,
    boxShadow: "0px 0px 0px ",
    ":hover": {
      ...base[":hover"],
      border: "1px solid var(--grey-40);",
      boxShadow: "0px 0px 0px ",
    },
  }),
  singleValue: (base: CSSObjectWithLabel, state: SingleValueProps<T, U, G>) => ({
    ...base,
    color: state.isDisabled ? "var(--grey-60)" : "var(--text)",
    // backgroundColor: hasVisibility(state.data) && state.data.visibility === "hidden" ? "var(--warning)" : undefined,
  }),
  option: (base: CSSObjectWithLabel, props: OptionProps<T, U, G>) => {
    const isHidden = isHiddenVisibilityOption(props.data);

    return {
      ...base,
      backgroundColor:
        props.isSelected && isHidden
          ? "var(--warning-dark)"
          : isHidden
            ? "var(--warning)"
            : props.isSelected
              ? "var(--grey-15)"
              : "white",
      color: "var(--text)",
      fontWeight: 300,
      ":hover": {
        ...base[":hover"],
        backgroundColor: isHidden ? "var(--warning-dark)" : props.isSelected ? "var(--grey-15)" : "var(--grey-10)",
      },
    };
  },
  menu: (base: CSSObjectWithLabel) => ({
    ...base,
    borderRadius: 0,
    marginTop: 0,
    marginBottom: 0,
  }),
  // Menu is portaled to document.body (see menuPortalTarget) to escape any ancestor's
  // overflow/z-index — this must clear ModalDialog's stacking z-index (baseZIndex 1000 +
  // stack depth) regardless of how many modals are nested, same reasoning as ListItemMenu.
  menuPortal: (base: CSSObjectWithLabel) => ({
    ...base,
    zIndex: 100000,
  }),
  menuList: (base: CSSObjectWithLabel) => ({
    ...base,
    paddingTop: 0,
    paddingBottom: 0,
  }),
  multiValue: (base: CSSObjectWithLabel, props: MultiValueProps<T, U, G>) => {
    const isHidden = isHiddenVisibilityOption(props.data);

    return {
      ...base,
      borderRadius: 0,
      backgroundColor: isHidden ? "var(--warning)" : "var(--grey-20)",
    };
  },
});
